import { useEffect, useState } from "react";
import Link from "next/link";
import CookieEssentialIcon from "../icons/CookieEssentialIcon";
import CookiePerformanceIcon from "../icons/CookiePerformanceIcon";
import CookieAdsIcon from "../icons/CookieAdsIcon";
import CookieAnalyticsIcon from "../icons/CookieAnalyticsIcon";
import CookieInfoIcon from "../icons/CookieInfoIcon";
import { ALL_GRANTED } from "@/utils/cookies/cookieConsent";

const CATEGORIES = [
  {
    key: "performance",
    Icon: CookiePerformanceIcon,
    boxClass: "bg-[rgba(104,208,0,0.1)] text-[#68D000]",
    en: { title: "Performance", desc: "Improve site speed and quality" },
    ar: { title: "الأداء الوظيفي", desc: "تحسين سرعة الموقع وجودته" },
  },
  {
    key: "ads",
    Icon: CookieAdsIcon,
    boxClass: "bg-[#faeeda] text-[#FFB333]",
    en: { title: "Personalized Ads", desc: "Offers tailored to your interests" },
    ar: { title: "الإعلانات المخصصة", desc: "عروض تناسب اهتماماتك" },
  },
  {
    key: "analytics",
    Icon: CookieAnalyticsIcon,
    boxClass: "bg-[#eeedfe] text-[#584DFF]",
    en: {
      title: "Analytics & Measurement",
      desc: "Understand how you use the site",
    },
    ar: { title: "التحليلات والقياس", desc: "نفهم كيف تستخدم الموقع" },
  },
];

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onChange}
    className={`relative h-[26px] w-[44px] shrink-0 rounded-[26px] transition-colors duration-200 ${
      checked ? "bg-primary" : "bg-[#e4e6ea]"
    }`}
  >
    <span
      className={`absolute top-[3px] size-[20px] rounded-[10px] bg-white transition-[inset-inline-start] duration-200 ${
        checked ? "start-[21px]" : "start-[3px]"
      }`}
    />
  </button>
);

/**
 * Cookie preferences modal.
 *
 * Owns the draft toggle state while open; nothing is persisted until the
 * visitor hits Save Changes, so dismissing via Escape/backdrop/X or following
 * one of the footer links leaves the previous answer untouched. "Essential" is
 * rendered as a static badge because it cannot be revoked.
 *
 * Rendered inline rather than through a portal: the parent sits directly under
 * the <main> element that defines the next/font CSS variables, so portalling to
 * document.body would drop the font and no ancestor creates a containing block
 * that could break `position: fixed`.
 *
 * Props:
 *   onSave  — (consent) => void, called with per-category booleans
 *   onClose — () => void, dismiss without persisting
 *   locale  — 'ar' | 'en'
 */
const CookieSettingModal = ({ onSave, onClose, locale }) => {
  const isEN = locale === "en";
  const [consent, setConsent] = useState(ALL_GRANTED);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const font = isEN ? "font-poppins" : "font-noto";

  return (
    <div className={`fixed inset-0 z-[9999999999] ${font}`}>
      <div
        className="fixed inset-0 bg-lightblack opacity-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={isEN ? "Privacy Preferences" : "تفضيلات الخصوصية"}
          className="relative flex max-h-full w-full max-w-[620px] flex-col overflow-y-auto rounded-[16px] bg-white"
        >
          <div className="relative flex w-full items-center justify-center bg-primary px-[24px] py-[18px]">
            <h2 className="text-[17px] font-semibold text-white">
              {isEN ? "Privacy Preferences" : "تفضيلات الخصوصية"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={isEN ? "Close" : "إغلاق"}
              className="absolute end-[24px] top-[18px] text-white"
            >
              <svg
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="flex w-full flex-col gap-[10px] px-[24px] pt-[15px]">
            <p className="text-[13px] leading-[22.1px] text-[#111827]">
              {isEN
                ? "We use cookies to improve your experience on our site. You can enable or disable each category at any time."
                : "نستخدم ملفات الارتباط لتحسين تجربتك لموقعنا. يمكنك تفعيل أو تعطيل كل فئة في أي وقت."}
            </p>
            {/* Forced LTR: this is the one row the design does not mirror —
                the icon sits on the physical right in both locales. The
                paragraph re-derives its own direction from its content. */}
            <div
              dir="ltr"
              className="flex w-full items-start gap-[8px] rounded-[8px] bg-[#e6f1fb] px-[14px] py-[10px]"
            >
              <p
                dir="auto"
                className="min-w-0 flex-1 text-[12px] leading-[19.2px] text-primary"
              >
                {isEN
                  ? "Temporarily saved during your current browsing session - so you don't have to repeat it on every page you visit."
                  : "نحتفظ مؤقتًا بتفضيلاتك خلال جلسة التصفح الحالية — حتى لا تضطر لتكرارها في كل صفحة تزورها."}
              </p>
              <CookieInfoIcon className="mt-[2px] shrink-0 text-[#378ADD]" />
            </div>
          </div>

          <div className="flex w-full flex-col gap-[10px] px-[24px] py-[14px]">
            <div className="flex w-full items-center gap-[12px] rounded-[12px] px-[16px] py-[14px]">
              <span className="flex size-[36px] shrink-0 items-center justify-center rounded-[8px] bg-[#e6f1fb] text-primary">
                <CookieEssentialIcon />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[14px] font-semibold text-black">
                  {isEN ? "Essential" : "أساسي"}
                </span>
                <span className="text-[12px] text-black">
                  {isEN ? "Required for the site to function" : "ضروري لعمل الموقع"}
                </span>
              </div>
              <span className="shrink-0 rounded-[20px] bg-[#e6f1fb] px-[10px] py-[4px] text-[11px] font-semibold text-primary">
                {isEN ? "Always active" : "مفعّل دائمًا"}
              </span>
            </div>

            {CATEGORIES.map(({ key, Icon, boxClass, ...copy }) => {
              const { title, desc } = isEN ? copy.en : copy.ar;
              return (
                <div
                  key={key}
                  className="flex w-full items-center gap-[12px] rounded-[12px] ps-[16px] pe-[19px] py-[14px]"
                >
                  <span
                    className={`flex size-[36px] shrink-0 items-center justify-center rounded-[8px] ${boxClass}`}
                  >
                    <Icon />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-[14px] font-semibold text-black">
                      {title}
                    </span>
                    <span className="text-[12px] text-black">{desc}</span>
                  </div>
                  <Toggle
                    checked={consent[key]}
                    label={title}
                    onChange={() =>
                      setConsent((prev) => ({ ...prev, [key]: !prev[key] }))
                    }
                  />
                </div>
              );
            })}
          </div>

          <div className="flex w-full flex-col gap-[12px] px-[24px] pb-[18px] pt-[14px]">
            <div className="flex w-full justify-center">
              <button
                type="button"
                onClick={() => onSave(consent)}
                className="h-[46px] w-[184px] rounded-[8px] bg-primary campaign:bg-campaignAccent text-[13px] font-semibold text-white"
              >
                {isEN ? "Save Changes" : "حفظ التغييرات"}
              </button>
            </div>
            {/* Dismiss on navigate: the modal is mounted from _app.js, so a
                client-side transition keeps it — and its scroll lock — on top
                of the destination page. Closing hands the page back to the
                reader and leaves the banner as the way back in. */}
            <div className="flex w-full justify-center gap-[20px]">
              <Link
                href="/static/privacy-policy"
                onClick={onClose}
                className="text-[12px] text-black underline"
              >
                {isEN ? "Privacy Policy" : "سياسة الخصوصية"}
              </Link>
              <Link
                href="/static/terms-conditions"
                onClick={onClose}
                className="text-[12px] text-black underline"
              >
                {isEN ? "Terms & Conditions" : "الشروط والأحكام"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingModal;
